import { oc } from "@orpc/contract";
import { z } from "zod";

// ── Shared enums ──

export const GuardrailKindSchema = z.enum([
  "openai_moderation",
  "regex",
  "prompt_injection",
  "content_safety",
]);
export type GuardrailKind = z.infer<typeof GuardrailKindSchema>;

export const GuardrailStageSchema = z.enum([
  "pre_step",
  "post_step",
  "pre_call",
  "post_call",
]);

export const GuardrailActionSchema = z.enum(["block", "review"]);

export const GuardrailTargetSourceSchema = z.enum([
  "step_input",
  "step_output",
  "call_input",
  "call_output",
]);

// ── Response schemas ──

export const GuardrailVersionSummarySchema = z.object({
  id: z.string().uuid(),
  versionNumber: z.number().int(),
  stage: GuardrailStageSchema,
  action: GuardrailActionSchema,
  targetSource: GuardrailTargetSourceSchema,
  targetPath: z.string().nullable(),
  config: z.record(z.string(), z.unknown()),
  enabled: z.boolean(),
  publishedBy: z.string().uuid().nullable(),
  publishedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
});
export type GuardrailVersionSummary = z.infer<
  typeof GuardrailVersionSummarySchema
>;

export const GuardrailAssignmentSummarySchema = z.object({
  id: z.string().uuid(),
  workflowStepId: z.string().uuid(),
  workflowStepName: z.string().nullable(),
  workflowId: z.string().uuid().nullable(),
  workflowDefinitionId: z.string().uuid().nullable(),
  workflowDefinitionName: z.string().nullable(),
  sortOrder: z.number().int(),
  enabled: z.boolean(),
});
export type GuardrailAssignmentSummary = z.infer<
  typeof GuardrailAssignmentSummarySchema
>;

export const GuardrailPolicySummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  kind: GuardrailKindSchema,
  latestVersion: GuardrailVersionSummarySchema.nullable(),
  versionCount: z.number().int(),
  assignmentCount: z.number().int(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type GuardrailPolicySummary = z.infer<
  typeof GuardrailPolicySummarySchema
>;

export const GuardrailPolicyDetailSchema = GuardrailPolicySummarySchema.extend({
  versions: z.array(GuardrailVersionSummarySchema),
  assignments: z.array(GuardrailAssignmentSummarySchema),
});
export type GuardrailPolicyDetail = z.infer<typeof GuardrailPolicyDetailSchema>;

// ── Input schemas ──

export const ListGuardrailPoliciesInputSchema = z.object({
  organizationId: z.string().uuid(),
  kind: GuardrailKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListGuardrailPoliciesInput = z.infer<
  typeof ListGuardrailPoliciesInputSchema
>;

export const ListGuardrailPoliciesResponseSchema = z.object({
  policies: z.array(GuardrailPolicySummarySchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});
export type ListGuardrailPoliciesResponse = z.infer<
  typeof ListGuardrailPoliciesResponseSchema
>;

export const GetGuardrailPolicyInputSchema = z.object({
  organizationId: z.string().uuid(),
  definitionId: z.string().uuid(),
});

export const CreateGuardrailPolicyInputSchema = z.object({
  organizationId: z.string().uuid(),
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  kind: GuardrailKindSchema,
  stage: GuardrailStageSchema,
  action: GuardrailActionSchema,
  targetSource: GuardrailTargetSourceSchema,
  targetPath: z.string().max(512).optional(),
  config: z.record(z.string(), z.unknown()),
});
export type CreateGuardrailPolicyInput = z.infer<
  typeof CreateGuardrailPolicyInputSchema
>;

export const UpdateGuardrailPolicyInputSchema = z.object({
  organizationId: z.string().uuid(),
  definitionId: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(2000).nullable().optional(),
  stage: GuardrailStageSchema,
  action: GuardrailActionSchema,
  targetSource: GuardrailTargetSourceSchema,
  targetPath: z.string().max(512).nullable().optional(),
  config: z.record(z.string(), z.unknown()),
});
export type UpdateGuardrailPolicyInput = z.infer<
  typeof UpdateGuardrailPolicyInputSchema
>;

export const DeleteGuardrailPolicyInputSchema = z.object({
  organizationId: z.string().uuid(),
  definitionId: z.string().uuid(),
});

export const DeleteGuardrailPolicyResponseSchema = z.object({
  success: z.literal(true),
  hadAssignments: z.boolean(),
});

// ── Step assignment schemas ──

export const ListStepAssignmentsInputSchema = z.object({
  organizationId: z.string().uuid(),
  workflowStepId: z.string().uuid(),
});

export const StepAssignmentSchema = z.object({
  id: z.string().uuid(),
  guardrailDefinitionId: z.string().uuid(),
  policyName: z.string(),
  policyKind: GuardrailKindSchema,
  sortOrder: z.number().int(),
  enabled: z.boolean(),
  latestVersion: GuardrailVersionSummarySchema.nullable(),
});
export type StepAssignment = z.infer<typeof StepAssignmentSchema>;

export const ListStepAssignmentsResponseSchema = z.object({
  assignments: z.array(StepAssignmentSchema),
});

export const SetStepAssignmentsInputSchema = z.object({
  organizationId: z.string().uuid(),
  workflowStepId: z.string().uuid(),
  assignments: z.array(
    z.object({
      guardrailDefinitionId: z.string().uuid(),
      sortOrder: z.number().int().min(0),
      enabled: z.boolean().default(true),
    }),
  ),
});

export const SetStepAssignmentsResponseSchema = z.object({
  success: z.literal(true),
  count: z.number().int(),
});

// ── Contract ──

export const guardrailPoliciesContracts = {
  list: oc
    .input(ListGuardrailPoliciesInputSchema)
    .output(ListGuardrailPoliciesResponseSchema)
    .route({
      method: "GET",
      summary: "List guardrail policies",
      description:
        "Pages through the org's workflow guardrail policies (moderation, regex, prompt-injection, content-safety), optionally by kind, with latest version and assignment counts.",
    }),
  get: oc
    .input(GetGuardrailPolicyInputSchema)
    .output(GuardrailPolicyDetailSchema)
    .route({
      method: "GET",
      summary: "Get a guardrail policy",
      description:
        "Returns one guardrail policy with every published version (stage, block or review action, target, config) and the workflow steps it is assigned to.",
    }),
  create: oc
    .input(CreateGuardrailPolicyInputSchema)
    .output(GuardrailPolicySummarySchema)
    .route({
      method: "POST",
      summary: "Create a guardrail policy",
      description:
        "Creates a guardrail policy and its first version: kind, check stage, block or review action, what it inspects, and kind-specific config.",
    }),
  update: oc
    .input(UpdateGuardrailPolicyInputSchema)
    .output(GuardrailPolicySummarySchema)
    .route({
      method: "POST",
      summary: "Update a guardrail policy",
      description:
        "Renames or re-describes a guardrail policy and publishes a new version with the given stage, action, target and config.",
    }),
  delete: oc
    .input(DeleteGuardrailPolicyInputSchema)
    .output(DeleteGuardrailPolicyResponseSchema)
    .route({
      method: "POST",
      summary: "Delete a guardrail policy",
      description:
        "Deletes a guardrail policy and removes it from every workflow step it guarded; reports whether it had assignments.",
    }),
  listStepAssignments: oc
    .input(ListStepAssignmentsInputSchema)
    .output(ListStepAssignmentsResponseSchema)
    .route({
      method: "GET",
      summary: "List guardrails on a workflow step",
      description:
        "Lists the guardrail policies assigned to one workflow step, in run order, with enabled flag and each policy's latest version.",
    }),
  setStepAssignments: oc
    .input(SetStepAssignmentsInputSchema)
    .output(SetStepAssignmentsResponseSchema)
    .route({
      method: "POST",
      summary: "Set guardrails on a workflow step",
      description:
        "Replaces the guardrail policies assigned to one workflow step with the given list, order and enabled flags; returns the new count.",
    }),
} as const;
